import {CallJs} from "../lib/types"
import * as React from "react"
import * as ReactDOM from "react-dom"
import * as Utils from "../lib/utils"
import {MultiSelect,Context,ContextFormat} from "../lib/sbhtml/multi-select"
import * as Immutable from "immutable"

export const renderer : CallJs<AccessConfigurerCallJsParams> = (replace, params) => {
	const div = document.createElement('div')
	replace(div)
	ReactDOM.render(
		<AccessConfigurerRenderer
			selectedAccessType={accessTypeFromName(params.accessType)}
			selectedMembershipTypes={Immutable.OrderedMap(params.selectedTypes)}
			allMembershipTypes={Immutable.OrderedMap(params.allTypes)}
			selectedAccessGroups={Immutable.OrderedMap(params.selectedGroups)}
			allAccessGroups={Immutable.OrderedMap(params.allGroups)}
		/>,
		div
	)
}

export interface AccessConfigurerCallJsParams {
	accessType:string
	selectedTypes:ContextFormat
	allTypes:ContextFormat
	selectedGroups:ContextFormat
	allGroups:ContextFormat
}

const accessTypeFromName : (_:string) => AccessType = (accessType) => {
		switch(accessType) {
		case 'none':
			return AccessType.none;
		case 'types':
			return AccessType.types;
		case 'groups':
			return AccessType.groups;
		case 'or':
			return AccessType.or;
	}
	
	throw new Error(`Could not recognise access type with name "${accessType}"`)
}

const enum AccessType {
	none,
	types,
	groups,
	or
}


/**
 *  Community2 Access Configurer Widget
 *
 *  A widget that allows an admin to configure the access restrictions for a given community2 group.
 *
 *  Access can be restricted by either MembershipType or Access Group
 *  There are four available criteria by which access restriction can be specified as represented by the
 *  AccessType enum.
 *  
 *   - None:             No restrictions; hide both multi-selects.
 *   - Membership Types: Restrict only by Membership Type; show only the membership types multi-select
 *   - Access Groups:    Restrict only by Access Group; show only access groups multi-select
 *   - Both (OR):        Restrict by either Membership or Access Group; show both multi-selects
 *   
 *  The widget displays a set of radio buttons for the selection of the AccessType,
 *  and depending on that selection, shows the appropriate multi-select widget(s) as indicated above.
 */


interface AccessConfigurerProps {
	selectedAccessType:AccessType
	selectedMembershipTypes:Context   // Those that will allow access to a member
	allMembershipTypes:Context        // The total set from which the above is select from
	selectedAccessGroups:Context      // same as above but for AccessGroups
	allAccessGroups:Context
}

interface AccessConfigurerState {
	selectedAccessType:AccessType
	selectedMembershipTypes:Context
	selectedAccessGroups:Context
	
	/*
	 * The multi-select widget provides the rendering of the different lists
	 * The functionality for moving items between the remaining and select lists is provided
	 * by the parent AccessConfigurator based on the old state and the action triggered,
	 * to return a new state.
	 * The new state is then reflected when the multi-select widget is re-rendered.
	 * 
	 * The state must therefore maintain not only the items selected, but also the highlighted items in a list.
	 * It will also ensure that should a render occur (that is not as a result of an Add / Remove), that the 
	 * highlighting is preserved. For example, selecting a different AccessType hides the current mutli-select. 
	 * Showing it again should show the original highlighting.
	 */
	remainingHighlightedMembershipTypes:Context
	selectedHighlightedMembershipTypes:Context
	remainingHighlightedAccessGroups:Context
	selectedHighlightedAccessGroups:Context
}

class AccessConfigurerRenderer extends React.Component<AccessConfigurerProps,AccessConfigurerState> {
	constructor(props:AccessConfigurerProps, context:any) {
		super(props, context)
		this.state = {
			selectedAccessType:props.selectedAccessType,
			selectedMembershipTypes:props.selectedMembershipTypes,
			selectedAccessGroups:props.selectedAccessGroups,

			remainingHighlightedMembershipTypes:Immutable.OrderedMap({} as ContextFormat),
			selectedHighlightedMembershipTypes:Immutable.OrderedMap({} as ContextFormat),
			remainingHighlightedAccessGroups:Immutable.OrderedMap({} as ContextFormat),
			selectedHighlightedAccessGroups:Immutable.OrderedMap({} as ContextFormat),
		}
	}

	render():JSX.Element {
		const radioRendererOnChangeHandler = (accessType:AccessType) => (e:React.FormEvent<{}>) => this.setState((state) => {state.selectedAccessType=accessType; return state;})
		
		const addTypesHandler = () => this.setState((state) => {
			state.selectedMembershipTypes = state.selectedMembershipTypes.merge(state.remainingHighlightedMembershipTypes)
			state.remainingHighlightedMembershipTypes = Immutable.OrderedMap({} as ContextFormat)
			return state
		})
		const addGroupsHandler = () => this.setState((state) => {
			state.selectedAccessGroups = state.selectedAccessGroups.merge(state.remainingHighlightedAccessGroups)
			state.remainingHighlightedAccessGroups = Immutable.OrderedMap({} as ContextFormat)
			return state
		})
		
		const removeTypesHandler = () => this.setState((state) => {
			state.selectedMembershipTypes = Utils.mapSubtract(state.selectedMembershipTypes,state.selectedHighlightedMembershipTypes)
			state.selectedHighlightedMembershipTypes = Immutable.OrderedMap({} as ContextFormat)
			return state
		})
		const removeGroupsHandler = () => this.setState((state) => {
			state.selectedAccessGroups = Utils.mapSubtract(state.selectedAccessGroups,state.selectedHighlightedAccessGroups)
			state.selectedHighlightedAccessGroups = Immutable.OrderedMap({} as ContextFormat)
			return state
		})
		
		const addSelectedTypesHandler = (newItems:Context) => this.setState((state) => {
			state.remainingHighlightedMembershipTypes = newItems;
			return state;
		})
		
		const addSelectedGroupsHandler = (newItems:Context) => this.setState((state) => { state.remainingHighlightedAccessGroups = newItems; return state; })

		const removeSelectedTypesHandler = (newItems:Context) => this.setState((state) => { state.selectedHighlightedMembershipTypes = newItems; return state; })
		const removeSelectedGroupsHandler = (newItems:Context) => this.setState((state) => { state.selectedHighlightedAccessGroups = newItems; return state; })
		
		const typesContainer  = <div>
			<label>Select Membership Type(s) and click on the 'Add >>' button</label>
			<MultiSelect
				name="typesSelectedValues"
				keyPrefix="type"
				selectedItems={this.state.selectedMembershipTypes}
				allItems={this.props.allMembershipTypes}
				remainingHighlightedItems={this.state.remainingHighlightedMembershipTypes}
				selectedHighlightedItems={this.state.selectedHighlightedMembershipTypes}
				addOptionsHandler={addTypesHandler}
				removeOptionsHandler={removeTypesHandler}
				remainingHighlightHandler={addSelectedTypesHandler}
				selectedHighlightHandler={removeSelectedTypesHandler}
			/>
		</div>
		const groupsContainer = <div>
			<label>Select Access Group(s) and click on the 'Add >>' button</label>
			<MultiSelect
				name="groupsSelectedValues"
				keyPrefix="group"
				selectedItems={this.state.selectedAccessGroups}
				allItems={this.props.allAccessGroups}
				remainingHighlightedItems={this.state.remainingHighlightedAccessGroups}
				selectedHighlightedItems={this.state.selectedHighlightedAccessGroups}
				addOptionsHandler={addGroupsHandler}
				removeOptionsHandler={removeGroupsHandler}
				remainingHighlightHandler={addSelectedGroupsHandler}
				selectedHighlightHandler={removeSelectedGroupsHandler}
			/>
		</div>
		const onMessage = <label>
			Note: User's must be in a selected Membership Type OR a selected Access Group
		</label>

		return <div>
			<AccessTypeRadioGroup selectedAccessType={this.state.selectedAccessType} onChangeHandler={radioRendererOnChangeHandler}/><br/>
			{this.state.selectedAccessType === AccessType.types || this.state.selectedAccessType === AccessType.or ? typesContainer : null}
			{this.state.selectedAccessType === AccessType.groups || this.state.selectedAccessType === AccessType.or ? groupsContainer : null}
			{this.state.selectedAccessType === AccessType.or ? onMessage : null}
		</div>
	}
}

/**
 *  A radio group that allows selection of one of the four access types (specified in the AccessConfigurer)
 */
interface AccessTypeRadioGroupProps {
	selectedAccessType:AccessType
	onChangeHandler:(_:AccessType) => React.FormEventHandler<{}>  //Fires when the selected value of the radio group changes
}

class AccessTypeRadioGroup extends React.Component<AccessTypeRadioGroupProps,{}> {
	render():JSX.Element {
		const name = 'accessType'
		const onChangeHandler = this.props.onChangeHandler
		
		return <div>
			<input onChange={onChangeHandler(AccessType.none)} id="none" type="radio" checked={this.props.selectedAccessType === AccessType.none} name={name} value="none"/>
			<label htmlFor="none">Do Not Limit Access to This Group</label><br/>
			<input onChange={onChangeHandler(AccessType.types)} id="types" type="radio" checked={this.props.selectedAccessType === AccessType.types} name={name} value="types"/>
			<label htmlFor="types">Limit Access by Membership Type(s)</label><br/>
			<input onChange={onChangeHandler(AccessType.groups)} id="groups" type="radio" checked={this.props.selectedAccessType === AccessType.groups} name={name} value="groups"/>
			<label htmlFor="groups">Limit Access by Access Group(s)</label><br/>
			<input onChange={onChangeHandler(AccessType.or)} id="or" type="radio" checked={this.props.selectedAccessType === AccessType.or} name={name} value="or"/>
			<label htmlFor="or">Limit Access by Membership Type(s) OR Access Group(s)</label><br/>
		</div>
	}
}



// WEBPACK FOOTER //
// ./src/community2/AccessConfigurer.tsx